// services/password.service.js
import { 
  fetchBucketContent, 
  fetchFileContent, 
  deleteFile, 
  encryptData, 
  decryptData,
  removeTxtExtension,
  ensureTxtExtension,
  clearBucketContentCache,
  getUserCredentials,
  ensureS3SafeFileName
} from './api.service';

import { 
  getCurrentInheritance, 
  uploadInheritance
} from './endowment.service';

import { UPLOAD_ENDPOINTS } from '../utils/constants';

const BUCKET_NAME = 'passwords';

// Fetch all passwords
export const fetchPasswords = async (token, forceClearCache = false) => {
  try {
    return await fetchBucketContent(BUCKET_NAME, token, forceClearCache);
  } catch (error) {
    console.error('Error fetching passwords:', error);
    throw error;
  }
};

// View a specific password
export const viewPassword = async (passwordName, token) => {
  try {
    const fileContent = await fetchFileContent(BUCKET_NAME, passwordName, token);
    return parsePasswordContent(fileContent, passwordName);
  } catch (error) {
    console.error('Error viewing password:', error);
    throw error;
  }
};

// Parse password file content
const parsePasswordContent = (fileContent, passwordName) => {
  try {
    const lines = fileContent.split(',');
    const ciphertext = lines[1];
    const userID = lines[2] || '';
    const accountNumber = lines[3] || '';
    const contactNumber = lines[4] || '';
    const contactName = lines[5] || '';
    
    // Decrypt the password
    const password = decryptData(ciphertext);
    
    return {
      alias: removeTxtExtension(passwordName),
      userID,
      password,
      accountNumber,
      contactNumber,
      contactName,
      originalName: passwordName // Keep the original name for updates
    };
  } catch (error) {
    console.error('Error parsing password content:', error);
    throw new Error('Invalid password file format');
  }
};

// Save password (create or update)
export const savePassword = async (passwordData, originalName = null, token) => {
  try {
    let credentials;
    if (!token) {
      console.warn('No token provided to fetchFileContent');
      // Try to get from session storage as fallback
      credentials = getUserCredentials();
      if (!credentials) {
        throw new Error('No authentication information found');
      }
      token = credentials.token;
    }
    
    // If we're updating, delete the old file first
    if (originalName) {
      await deletePassword(originalName, token);
    }
    
    // Encrypt the password
    const encryptedPassword = encryptData(passwordData.password);
    
    // Prepare data for API
    const data = {
      alias: passwordData.alias,
      password: encryptedPassword,
      userID: passwordData.userID,
      accountNumber: passwordData.accountNumber || '',
      contactNumber: passwordData.contactNumber || '',
      contactName: passwordData.contactName || ''
    };
    
    // Send to API
    const response = await fetch(UPLOAD_ENDPOINTS.UPLOAD_PASSWORD, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    // Clear cache after successful save
    await clearBucketContentCache();
    
    return true;
  } catch (error) {
    console.error('Error saving password:', error);
    throw error;
  }
};

/**
 * Add password to the user's current inheritance (like endowment page)
 * @param {string} passwordName - Password name/filename
 * @param {string} token - Authentication token
 * @returns {Promise<boolean>} Success status
 */
export const addPasswordToCurrentInheritance = async (passwordName, token) => {
  try {
    if (!token) {
      const credentials = getUserCredentials();
      if (!credentials) {
        throw new Error('No authentication information found');
      }
      token = credentials.token;
    }

    const userDetails = JSON.parse(sessionStorage.getItem('userDetails') || '{}');
    const userEmail = userDetails.Email || '';
    
    if (!userEmail) {
      throw new Error('User email not found in session');
    }

    // Get the current (most recent) inheritance for this user
    const currentInheritance = await getCurrentInheritance(token, userEmail);
    
    if (!currentInheritance) {
      throw new Error('No active endowment found. Please create an endowment first in the Endowment Centre.');
    }

    // Use the password name as filename (should already have .txt extension)
    const passwordToAdd = ensureTxtExtension(passwordName);

    // Check if the password is already in the inheritance
    if (currentInheritance.selectedItems.passwords && currentInheritance.selectedItems.passwords.includes(passwordToAdd)) {
      throw new Error('Password is already added to this endowment');
    }

    // Add the password to the existing password items (same logic as handleAddItems in useEndowment)
    const updatedItems = {
      files: [...(currentInheritance.selectedItems.files || [])],
      passwords: [...(currentInheritance.selectedItems.passwords || []), passwordToAdd],  // Add the new password
      banking: [...(currentInheritance.selectedItems.banking || [])],
      blockchain: [...(currentInheritance.selectedItems.blockchain || [])]
    };

    // Use uploadInheritance to update the inheritance with the new password
    // This works exactly like handleAddItems in useEndowment.js
    await uploadInheritance({
      alias: currentInheritance.alias,
      account: currentInheritance.account,
      selectedItems: updatedItems,
      duration: currentInheritance.duration,
      Email: userEmail
    }, token);

    return true;
  } catch (error) {
    console.error('❌ Error adding password to current inheritance:', error);
    throw error;
  }
};

/**
 * Update inheritance after password deletion
 * @param {string} passwordName - Deleted password name
 * @param {string} token - Authentication token
 * @returns {Promise<boolean>} Success status
 */
const updateInheritancesAfterPasswordDeletion = async (passwordName, token) => {
  try {
    if (!token) {
      const credentials = getUserCredentials();
      if (!credentials) {
        throw new Error('No authentication information found');
      }
      token = credentials.token;
    }

    const userDetails = JSON.parse(sessionStorage.getItem('userDetails') || '{}');
    const userEmail = userDetails.Email || '';
    
    if (!userEmail) {
      throw new Error('User email not found in session');
    }

    // Get the current inheritance for this user
    const currentInheritance = await getCurrentInheritance(token, userEmail);
    
    if (!currentInheritance) {
      return true;
    }

    const passwordToRemove = ensureTxtExtension(passwordName);
    const passwordIndex = currentInheritance.selectedItems.passwords?.indexOf(passwordToRemove) ?? -1;
    
    if (passwordIndex === -1) {
      return true;
    }
    
    
    // Remove the password from the inheritance
    const updatedItems = {
      files: [...(currentInheritance.selectedItems.files || [])],
      passwords: currentInheritance.selectedItems.passwords.filter(pwd => pwd !== passwordToRemove),
      banking: [...(currentInheritance.selectedItems.banking || [])],
      blockchain: [...(currentInheritance.selectedItems.blockchain || [])]
    };
    
    // Use uploadInheritance to update the inheritance without the deleted password
    await uploadInheritance({
      alias: currentInheritance.alias,
      account: currentInheritance.account,
      selectedItems: updatedItems,
      duration: currentInheritance.duration,
      Email: userEmail
    }, token);
    
    return true;
  } catch (error) {
    console.error('❌ Error updating inheritance after password deletion:', error);
    throw error;
  }
};

// Delete password
export const deletePassword = async (passwordName, token) => {
  try {
    // First, update any inheritances that contain this password
    try {
      await updateInheritancesAfterPasswordDeletion(passwordName, token);
    } catch (error) {
      console.error('Error updating inheritances:', error);
      // Continue with deletion even if inheritance update fails
    }
    
    return await deleteFile(BUCKET_NAME, passwordName, token);
  } catch (error) {
    console.error('Error deleting password:', error);
    throw error;
  }
};

// Generate a secure password
export const generatePassword = (length = 32) => {
  const charset = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*()_+~`|}{[]:;?><,./-=";
  let password = "";
  for (let i = 0; i < length; i++) {
    const randomIndex = Math.floor(Math.random() * charset.length);
    password += charset[randomIndex];
  }
  return password;
};

// Check if password alias already exists
export const checkPasswordAliasExists = async (alias, token) => {
  try {
    const passwords = await fetchPasswords(token);
    return passwords.some(password => 
      removeTxtExtension(password.name).toLowerCase() === alias.toLowerCase()
    );
  } catch (error) {
    console.error('Error checking password alias:', error);
    throw error;
  }
};

// Generate unique password alias
export const generateUniquePasswordAlias = async (proposedAlias, token) => {
  try {
    // Make sure we're working with a clean alias (no .txt extension)
    proposedAlias = removeTxtExtension(proposedAlias.trim());
    proposedAlias = ensureS3SafeFileName(proposedAlias);
    
    if (proposedAlias === '') {
      return 'Untitled';
    }
    
    const passwords = await fetchPasswords(token);
    
    // Get all existing password aliases without .txt extension for comparison
    const existingAliases = passwords.map(password => 
      removeTxtExtension(password.name).toLowerCase()
    );
    
    // Check if the base alias already exists
    if (!existingAliases.includes(proposedAlias.toLowerCase())) {
      return proposedAlias;
    }
    
    // Name exists, find a unique variation
    let counter = 1;
    let uniqueAlias = `${proposedAlias}(${counter})`;
    
    // Keep incrementing counter until we find a unique name
    while (existingAliases.includes(uniqueAlias.toLowerCase())) {
      counter++;
      uniqueAlias = `${proposedAlias}(${counter})`;
    }
    
    return uniqueAlias;
  } catch (error) {
    console.error('Error generating unique alias:', error);
    throw error;
  }
};